/**
 * @fileoverview System-web hook for reading and writing the current user's theme preference.
 * @module system-web/hooks/useUserThemePreference
 *
 * Responsibilities:
 * - Subscribe to the current user's `system_users` record via Dexie liveQuery.
 * - Expose the active user theme override and a setter that writes Dexie-first.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - @firesquirrel/local-db
 * - system-web/auth/AuthProvider
 * - system-web/services/systemUserService
 *
 * Architecture:
 * - Uses liveQuery to reactively subscribe to system_users for the current user.
 * - Writes through systemUserService.updateSystemUserThemePreference (Dexie-first,
 *   then wakeLocalWrite).
 * - Identifies the user by canonical login-sync id first, then normalized email.
 *
 * Notes:
 * - system-web has no tenant default concept (system users have no department affiliation);
 *   the only fallback is the app default ('default' theme).
 * - If the user record is absent in Dexie (initial sync pending), userThemeId is null
 *   and setUserThemeId logs a warning and returns without writing.
 */

import { useState, useEffect, useCallback } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDb } from '@firesquirrel/local-db';
import { useAuth } from '../auth/AuthProvider';
import { updateSystemUserThemePreference } from '../services/systemUserService';

/**
 * @contract SystemUserThemePreferenceState
 *
 * Purpose:
 * - Shape returned by useUserThemePreference in system-web.
 *
 * Fields:
 * - userThemeId: User theme override or null if not set.
 * - userId: System user id from Dexie or null while loading.
 * - setUserThemeId: Async Dexie-first setter (pass null to clear the override).
 * - loading: True while the initial Dexie query is resolving.
 * - error: Non-null when the Dexie subscription fails.
 * - resolved: True only when an authenticated user's local record has been found.
 *
 * Rules:
 * - setUserThemeId is idempotent.
 *
 * Sync:
 * - Writes propagate through system_users → /sync → system DB users.
 */
export interface SystemUserThemePreferenceState {
  userThemeId: string | null;
  userId: string | null;
  setUserThemeId: (themeId: string | null) => Promise<void>;
  loading: boolean;
  error: string | null;
  resolved: boolean;
}

/**
 * @hook useUserThemePreference
 *
 * Purpose:
 * - Provide reactive access to the current system admin user's theme override
 *   stored in system_users.preferences.themeId, with a Dexie-first write path.
 *
 * Returns:
 * - userThemeId, userId, setUserThemeId, loading, error
 *
 * Side Effects:
 * - Subscribes a Dexie liveQuery for the current user's system_users record.
 * - Writes to Dexie and triggers background sync when setUserThemeId is called.
 *
 * Offline Behavior:
 * - Reads from local Dexie — available offline.
 * - Writes to local Dexie immediately; sync to server occurs when connectivity resumes.
 */
export function useUserThemePreference(): SystemUserThemePreferenceState {
  const { user } = useAuth();
  const authUserId = user?.id || null;
  const authEmail = user?.email ?? null;

  const [userThemeId, setUserThemeIdState] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setUserId(null);
    setError(null);

    if (!authUserId && !authEmail) {
      setUserThemeIdState(null);
      setUserId(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let subscription: { unsubscribe: () => void } | null = null;

    getSystemDb()
      .then((db) => {
        if (cancelled) return;
        subscription = liveQuery(async () => {
          if (authUserId) {
            const byId = await db.system_users.get(authUserId) as any;
            if (byId && !byId._deleted) {
              return byId;
            }
          }

          const docs = await db.system_users.toArray() as any[];
          const normalizedAuthEmail = String(authEmail ?? '').trim().toLowerCase();
          const matched = docs.find((d: any) => (
            !d._deleted && String(d.email ?? '').trim().toLowerCase() === normalizedAuthEmail
          )) ?? null;
          return matched;
        }).subscribe({
          next: (doc: any) => {
            if (!cancelled) {
              setUserThemeIdState(doc?.preferences?.themeId ?? null);
              setUserId(doc?._id ?? null);
              setLoading(false);
            }
          },
          error: (err: unknown) => {
            if (!cancelled) {
              console.warn('[useUserThemePreference] liveQuery error:', err);
              setError(err instanceof Error ? err.message : 'Failed to load user theme preference');
              setLoading(false);
            }
          },
        });
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[useUserThemePreference] getSystemDb error:', err);
          setError(err instanceof Error ? err.message : 'Failed to open Dexie');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [authUserId, authEmail]);

  const setUserThemeId = useCallback(
    async (themeId: string | null): Promise<void> => {
      if (!userId) {
        throw new Error('Cannot save theme preference before the system user record is available');
      }
      const result = await updateSystemUserThemePreference(userId, themeId ?? null);
      if (result === null) {
        throw new Error('Cannot save theme preference because the system user record was not found');
      }
    },
    [userId],
  );

  return {
    userThemeId,
    userId,
    setUserThemeId,
    loading,
    error,
    resolved: !loading && userId !== null,
  };
}
